import dayjs from 'dayjs';
import { Calendar, Filter, Hash, RotateCcw, Search } from 'lucide-react';
import { useState } from 'react';

import DatePopoverSingle from '@/components/DatePopoverSingle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

interface FilterBoxProps {
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  startDate: string;
  endDate: string;
  isLoading: boolean;
  invoiceId: number | null;
  setInvoiceId: React.Dispatch<React.SetStateAction<number | null>>;
  resetFilters: () => void;
}

function FilterBox({
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  isLoading,
  invoiceId,
  setInvoiceId,
  resetFilters,
}: FilterBoxProps) {
  const [inputValue, setInputValue] = useState<number | null>(invoiceId);
  const [startDateForm, setStartDateForm] = useState<string>(
    dayjs(startDate).format('MM-DD-YYYY'),
  );
  const [endDateForm, setEndDateForm] = useState<string>(
    dayjs(endDate).format('MM-DD-YYYY'),
  );

  const isSearchAble =
    startDate !== startDateForm ||
    endDate !== endDateForm ||
    invoiceId !== inputValue;

  const isResetAble =
    !isLoading &&
    !(
      startDateForm === dayjs().subtract(7, 'day').format('MM-DD-YYYY') &&
      endDateForm === dayjs().format('MM-DD-YYYY') &&
      inputValue === null
    );

  return (
    <Card className="border-0 bg-gradient-to-br from-white/80 to-slate-50/80 shadow-sm backdrop-blur-sm dark:from-slate-900/80 dark:to-slate-800/80">
      <CardContent className="p-3">
        {/* Header */}
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
            <Filter className="h-3 w-3 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              تصفية الفواتير
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              ابحث وصفف فواتير الوارد حسب التاريخ والرقم
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {/* Invoice ID Search */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Hash className="h-3 w-3 text-blue-600 dark:text-blue-400" />
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                رقم الفاتورة
              </label>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
              <Input
                type="number"
                autoFocus
                value={inputValue || ''}
                placeholder="أدخل رقم الفاتورة..."
                className="border-slate-200 bg-white/50 pl-6 pr-2 text-center text-sm font-medium tracking-wider placeholder:text-slate-400 focus:border-blue-500 focus:bg-white dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:bg-slate-800"
                onChange={(e) => {
                  if (e.target.value) {
                    setInputValue(+e.target.value);
                  } else {
                    setInputValue(null);
                  }
                }}
              />
            </div>
          </div>

          <Separator className="bg-slate-200 dark:bg-slate-700" />

          {/* Date Range Selection */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                نطاق التاريخ
              </label>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {/* Start Date */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                  >
                    من
                  </Badge>
                </div>
                <div className="relative">
                  <DatePopoverSingle
                    value={dayjs(startDateForm).format('YYYY-MM-DD')}
                    selectFn={(dateString) => {
                      setStartDateForm(dayjs(dateString).format('MM-DD-YYYY'));
                    }}
                    placeholder="اختر تاريخ البداية"
                    width="w-full"
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                  >
                    إلى
                  </Badge>
                </div>
                <div className="relative">
                  <DatePopoverSingle
                    value={dayjs(endDateForm).format('YYYY-MM-DD')}
                    selectFn={(dateString) => {
                      setEndDateForm(dayjs(dateString).format('MM-DD-YYYY'));
                    }}
                    placeholder="اختر تاريخ النهاية"
                    width="w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-200 dark:bg-slate-700" />

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            <Button
              size="sm"
              className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm transition-all duration-300 hover:from-blue-600 hover:to-indigo-700 hover:shadow-md disabled:opacity-50"
              disabled={!isSearchAble || isLoading}
              onClick={() => {
                setStartDate(startDateForm);
                setEndDate(endDateForm);
                setInvoiceId(inputValue);
              }}
            >
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 transition-transform duration-700 group-hover:translate-x-full"></div>
              <Search className="mr-1.5 h-3 w-3" />
              {isLoading ? 'جاري البحث...' : 'بحث'}
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="group relative overflow-hidden border-slate-300 bg-white/50 text-slate-700 shadow-sm transition-all duration-300 hover:bg-slate-50 hover:shadow-md dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:bg-slate-700/50"
              disabled={!isResetAble}
              onClick={() => {
                resetFilters();
                setInputValue(null);
                setStartDateForm(
                  dayjs().subtract(7, 'day').format('MM-DD-YYYY'),
                );
                setEndDateForm(dayjs().format('MM-DD-YYYY'));
              }}
            >
              <RotateCcw className="mr-1.5 h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
              إعادة تعيين
            </Button>
          </div>

          {/* Status Indicator */}
          {isSearchAble && (
            <div className="rounded border border-amber-200 bg-amber-50/50 p-1.5 dark:border-amber-800 dark:bg-amber-950/20">
              <div className="flex items-center gap-1.5">
                <div className="h-1 w-1 animate-pulse rounded-full bg-amber-500"></div>
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  تم تغيير الفلاتر - اضغط بحث لتطبيق التغييرات
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default FilterBox;
